import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from '@emotion/css';
import { html, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

import type { ReorderIndicatorRect } from './types.js';

/**
 * A thin primary-colored line that marks the drop position during a
 * row-reorder. Absolutely positioned inside the view host — lives with
 * the scroll transform so the indicator stays pinned to its row slot.
 */
export class RowReorderIndicator extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @state()
  accessor rect: ReorderIndicatorRect | null = null;

  show(rect: ReorderIndicatorRect): void {
    this.rect = rect;
  }

  hide(): void {
    this.rect = null;
  }

  override render(): TemplateResult {
    if (!this.rect) return html``;
    const { x, y, length, orientation } = this.rect;
    const style =
      orientation === 'horizontal'
        ? `left:${x}px; top:${y}px; width:${length}px; height:2px`
        : `left:${x}px; top:${y}px; width:2px; height:${length}px`;
    return html`<div class=${indicatorStyle} style=${style}></div>`;
  }
}

const indicatorStyle = css({
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: 10,
  borderRadius: '2px',

  '&&&': {
    background: 'var(--streamlain-primary-color)',
    boxShadow: '0 0 0 1px var(--streamlain-background-primary-color)',
  },
});

declare global {
  interface HTMLElementTagNameMap {
    'dv-row-reorder-indicator': RowReorderIndicator;
  }
}
